<script setup lang="ts" generic="TItem extends { [key: string]: any }">
import { QIcon, QItem, QSelect, QVirtualScroll } from 'quasar'
import { computed, nextTick, ref, useSlots } from 'vue'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import type { PValidationRule } from '../../types/types'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import { useHandleScrollQSelect } from '../../composables/menu/handleScrollQSelect'
import { useMenuMaxHeight } from '../../composables/menu/useMenuMaxHeight'
import PCard from '../card/PCard.vue'
import { PCardSize } from '../card/types'
import PDivider from '../divider/PDivider.vue'
import PIcon from '../icon/PIcon.vue'
import PTextFormBody from '../typography/form/PTextFormBody.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PMaxLines } from '../typography/types'
import type { PMenuAnchor } from '../menu/types'
import type { PSelectGroup } from './types'
import PTextInputField from './PTextInputField.vue'
import InputFieldLabel from './components/LabelInputField.vue'
import { menuOffset } from './composable/menuOffset'
import CreateBtn from './components/CreateBtn.vue'
import MobileSuggestionDialog from './components/MobileSuggestionDialog.vue'

type GroupHeader = {
  __isGroupHeader: true
  key: string
  label: string
  __isFirstGroup: boolean
}
type GroupedOption<TItem> = {
  __isGroupOption: true
  __value: string | number
  __label: string
  __groupKey: string
  __original: TItem
  __isFirstInGroup: boolean
  __isLastInGroup: boolean
  __isLastGroup: boolean
}

type Props = {
  items: TItem[]
  itemValue: keyof TItem
  itemText: ((item: TItem) => string) | keyof TItem
  tooltip?: null | string
  label?: null | string
  placeholder?: null | string
  hint?: null | string
  rules?: PValidationRule[]
  loading?: boolean
  noData?: null | string
  disable?: boolean
  readonly?: boolean
  debounce?: null | number
  disableClearable?: boolean
  errorMessage?: null | string
  create?: null | string
  isCreateVisible?: boolean
  createEndIcon?: null | string
  autofocus?: boolean
  returnObject?: boolean
  nonEditableSelection?: boolean
  openMobileDialogImmediately?: boolean
  defaultFilter?: boolean
  groups?: PSelectGroup<TItem>[]
  menuAnchor?: PMenuAnchor
  menuSelf?: PMenuAnchor
  menuWidth?: null | number
  cy: string
}

type Emits = {
  (e: 'filter', val: null | string): void
  (e: 'create', val: null | string): void
  (e: 'blur'): void
  (e: 'mobile-dialog-dismiss'): void
}

const model = defineModel<TItem | string | number | null>({ default: null })
const groupModel = defineModel<string | null>('group', { default: null })

const props = withDefaults(defineProps<Props>(), {
  disable: false,
  readonly: false,
  disableClearable: false,
  isCreateVisible: true,
  loading: false,
  autofocus: false,
  returnObject: false,
  nonEditableSelection: false,
  openMobileDialogImmediately: false,
  defaultFilter: false,
  rules() {
    return []
  },
})

const emits = defineEmits<Emits>()

const slots = useSlots()
const ds = usePrevioDS()

const qSelectComponent = ref<InstanceType<typeof QSelect>>()
const handleScrollQSelect = useHandleScrollQSelect(qSelectComponent)
const menuMaxHeight = useMenuMaxHeight(() => qSelectComponent.value?.$el as HTMLElement | undefined)

const popupContentClass = computed<string>(() => {
  return ['p-pa-sm', menuMaxHeight.popupContentClass.value].filter(Boolean).join(' ')
})

const popupContentStyle = computed<Record<string, string>[]>(() => {
  const style: Record<string, string>[] = []

  if (menuMaxHeight.popupContentStyle.value) {
    style.push(menuMaxHeight.popupContentStyle.value)
  }

  if (props.menuWidth) {
    style.push({ width: `${props.menuWidth}px` })
  }

  return style
})

const inputValue = ref<null | string>(null)
const isMobileDialogShow = ref<boolean>(
  ds.screen().isMobile && !props.readonly ? props.openMobileDialogImmediately : false,
)

const isFocused = ref<boolean>(false)
let isSelecting = false

const isGroupHeader = (item: TItem | GroupHeader | GroupedOption<TItem>): item is GroupHeader => {
  return typeof item === 'object' && item !== null && '__isGroupHeader' in item
}

const isGroupedOption = (
  item: TItem | GroupHeader | GroupedOption<TItem>,
): item is GroupedOption<TItem> => {
  return typeof item === 'object' && item !== null && '__isGroupOption' in item
}

const groupItemLabel = (item: TItem, group: PSelectGroup<TItem>): string => {
  return typeof group.itemText === 'function'
    ? group.itemText(item)
    : (item[group.itemText] as string)
}

const inputDebounce = computed<number>(() => {
  if (props.debounce === undefined) {
    // no explicit debounce given - use 0 if data can be assumed to be local
    return props.defaultFilter ? 0 : ds.debounce.API_DEBOUNCE
  }

  return props.debounce ?? 0
})

const itemLabel = (item: TItem): string => {
  return typeof props.itemText === 'function' ? props.itemText(item) : item[props.itemText]
}

const optionLabel = computed<string | ((item: TItem) => string)>(() => {
  if (props.groups) {
    return '__label'
  }

  return typeof props.itemText === 'function' ? props.itemText : (props.itemText as string)
})

const allItems = computed<TItem[]>(() => {
  if (!props.groups) {
    return props.items
  }
  return props.groups.flatMap((g) => g.items)
})

const selectedItem = computed<TItem | null>(() => {
  if (props.returnObject) {
    return model.value as TItem
  }
  if (!props.groups) {
    return props.items.find((i) => i[props.itemValue] === model.value) ?? null
  }
  for (const group of props.groups) {
    const found = group.items.find((i) => i[group.itemValue] === model.value)
    if (found) {
      return found
    }
  }
  return null
})

const selectedGroupConfig = computed<PSelectGroup<TItem> | null>(() => {
  if (!props.groups || model.value === null) {
    return null
  }
  return props.groups.find((g) => g.items.some((i) => i[g.itemValue] === model.value)) ?? null
})

const selectedItemLabel = computed<string | null>(() => {
  if (!selectedItem.value) {
    return null
  }

  if (selectedGroupConfig.value) {
    return groupItemLabel(selectedItem.value, selectedGroupConfig.value)
  }

  return itemLabel(selectedItem.value)
})

const isSelectedText = (text: string): boolean => {
  return (
    selectedItem.value !== null &&
    (selectedItemLabel.value ?? itemLabel(selectedItem.value)) === text
  )
}

const buildGroupedOptions = (
  filterNeedle: string | null,
): (GroupHeader | GroupedOption<TItem>)[] => {
  const result: (GroupHeader | GroupedOption<TItem>)[] = []

  const nonEmptyGroups = props
    .groups!.map((group) => ({
      group,
      groupItems: filterNeedle
        ? group.items.filter(
            (item) => groupItemLabel(item, group).toLowerCase().indexOf(filterNeedle) > -1,
          )
        : group.items,
    }))
    .filter(({ groupItems }) => groupItems.length > 0)

  const lastGroupIndex = nonEmptyGroups.length - 1

  for (const [flatIndex, { group, groupItems }] of nonEmptyGroups.entries()) {
    result.push({
      __isGroupHeader: true,
      key: group.key,
      label: group.label,
      __isFirstGroup: flatIndex === 0,
    })

    const lastIndex = groupItems.length - 1

    for (const [index, item] of groupItems.entries()) {
      result.push({
        __isGroupOption: true,
        __value: item[group.itemValue] as string | number,
        __label: groupItemLabel(item, group),
        __groupKey: group.key,
        __original: item,
        __isFirstInGroup: index === 0,
        __isLastInGroup: index === lastIndex,
        __isLastGroup: flatIndex === lastGroupIndex,
      })
    }
  }

  return result
}

const itemsFiltered = computed<TItem[]>(() => {
  if (
    props.defaultFilter &&
    inputValue.value !== null &&
    inputValue.value !== '' &&
    !isSelectedText(inputValue.value)
  ) {
    const needle = inputValue.value.toLowerCase()
    return allItems.value.filter((item) => itemLabel(item)?.toLowerCase().indexOf(needle) > -1)
  }
  return allItems.value
})

const flatGroupOptions = computed<(TItem | GroupHeader | GroupedOption<TItem>)[]>(() => {
  if (!props.groups) {
    return itemsFiltered.value
  }

  const needle =
    props.defaultFilter &&
    inputValue.value !== null &&
    inputValue.value !== '' &&
    !isSelectedText(inputValue.value)
      ? inputValue.value.toLowerCase()
      : null

  return buildGroupedOptions(needle)
})

const isRequired = computed(() => {
  return props.rules.includes(ds.rules.required)
})

const hasAnyErrors = computed<boolean>(() => {
  return !!props.errorMessage
})

const isCreateBtnVisible = computed<boolean>(() => {
  return props.isCreateVisible && !!props.create
})

const hasInputValue = computed<boolean>(() => {
  return inputValue.value !== null && inputValue.value !== ''
})

// Quasar opens the menu whenever the no-option slot exists, even when it renders nothing
const isNoOptionSlotVisible = computed<boolean>(() => {
  return isCreateBtnVisible.value || hasInputValue.value
})

const isSelected = (id: string | number): boolean => {
  if (props.returnObject) {
    return (model.value as TItem)?.[props.itemValue] === id
  }
  return model.value === id
}

const updateGroupModel = (item: TItem): void => {
  if (!props.groups) {
    return
  }
  const matchedGroup = props.groups.find((g) =>
    g.items.some((i) => i[g.itemValue] === item[g.itemValue]),
  )
  groupModel.value = matchedGroup?.key ?? null
}

const selectItem = (item: TItem): void => {
  if (props.returnObject) {
    model.value = item
  } else {
    model.value = item[props.itemValue] as string | number
  }
  updateGroupModel(item)
  isMobileDialogShow.value = false

  nextTick(() => {
    qSelectComponent.value?.resetValidation()
  })
}

const selectGroupedItem = (opt: GroupedOption<TItem>): void => {
  model.value = props.returnObject ? opt.__original : opt.__value
  groupModel.value = opt.__groupKey
  isMobileDialogShow.value = false

  nextTick(() => {
    qSelectComponent.value?.resetValidation()
  })
}

const onInput = (value: string | number | null | undefined): void => {
  if (value == null || value === '') {
    value = null
  } else {
    value = value.toString()
  }

  const isNewValue = value !== inputValue.value
  inputValue.value = value

  // showPopup() checks the no-option slot synchronously, so it has to wait for the re-render
  nextTick(() => {
    const isSelectedLabel = value !== null && isSelectedText(value)

    if (isNewValue && !isSelectedLabel) {
      emits('filter', value)
    }

    // Quasar sets the input to the picked label (or clears it) right before closing the menu
    if (isSelecting || isSelectedLabel) {
      return
    }

    if (ds.screen().isLaptopAndLarge && (isFocused.value || props.autofocus)) {
      qSelectComponent.value?.showPopup()
    }
  })
}

const onCreate = (value: string | null): void => {
  if (!isCreateBtnVisible.value) {
    return
  }

  emits('create', value)
}

const isEnableClearable = computed<boolean>(() => {
  if (props.disableClearable || props.readonly || isRequired.value || props.loading) {
    return false
  }

  return model.value !== null
})

const onSelect = (value: TItem | string | number | null): void => {
  isSelecting = true
  nextTick(() => {
    isSelecting = false
  })

  model.value = value
  if (value !== null && props.groups) {
    const rawValue = props.returnObject
      ? (value as TItem)[props.itemValue]
      : (value as string | number)
    const matchedGroup = props.groups.find((g) => g.items.some((i) => i[g.itemValue] === rawValue))
    groupModel.value = matchedGroup?.key ?? null
  } else if (value === null) {
    groupModel.value = null
  }
}

const clear = (): void => {
  model.value = null
  groupModel.value = null

  // After click in clear icon we don`t want to show popup (Quasar show it automatically)
  setTimeout(() => {
    qSelectComponent.value?.hidePopup()
  }, 1)
}

const onMenuShow = (): void => {
  if (ds.screen().isMobile) {
    qSelectComponent.value?.hidePopup()
    isMobileDialogShow.value = true

    return
  }

  handleScrollQSelect.subscribe()
  menuMaxHeight.subscribe()
}

const onMenuHide = (): void => {
  if (ds.screen().isMobile) {
    return
  }

  if (props.nonEditableSelection && model.value !== null) {
    qSelectComponent.value?.blur()
  }

  handleScrollQSelect.unsubscribe()
  menuMaxHeight.unsubscribe()
}

const hidePopup = (): void => {
  qSelectComponent.value?.hidePopup()
  if (ds.screen().isMobile) {
    isMobileDialogShow.value = false
  }
}

const showSuggester = (): void => {
  if (props.readonly) {
    return
  }

  qSelectComponent.value?.showPopup()

  if (ds.screen().isMobile) {
    isMobileDialogShow.value = true
  }
}

const setInputValue = (val: string | null): void => {
  inputValue.value = val
  if (ds.screen().isLaptopAndLarge) {
    nextTick(() => {
      qSelectComponent.value?.updateInputValue(val ?? '', true)
    })
  }
  emits('filter', val)
}

defineExpose({ hidePopup, showSuggester, setInputValue })
</script>

<template>
  <input-field-label :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-select
    ref="qSelectComponent"
    popup-no-route-dismiss
    :model-value="model"
    :rules="rules"
    lazy-rules
    :placeholder="!nonEditableSelection || model === null ? placeholder : undefined"
    :hint="hint ?? undefined"
    :options="flatGroupOptions"
    :option-value="groups ? '__value' : (itemValue as string)"
    :option-label="optionLabel"
    :option-disable="(opt: TItem | GroupHeader | GroupedOption<TItem>) => isGroupHeader(opt)"
    :error="hasAnyErrors"
    :error-message="errorMessage ?? undefined"
    :loading="loading"
    :disable="disable"
    :readonly="readonly"
    :class="{
      'ds-input-field--readonly': readonly,
      'ds-suggestion-field--inline-selection': nonEditableSelection,
    }"
    :bg-color="Color.WHITE"
    :autofocus="autofocus"
    :input-debounce="inputDebounce"
    :data-cy="cy"
    :use-input="ds.screen().isLaptopAndLarge"
    :hide-selected="ds.screen().isLaptopAndLarge && (!nonEditableSelection || isFocused)"
    behavior="menu"
    :menu-anchor="menuAnchor"
    :menu-self="menuSelf"
    :menu-offset="menuOffset"
    :popup-content-class="popupContentClass"
    :popup-content-style="popupContentStyle"
    autocomplete="nope"
    :fill-input="!nonEditableSelection"
    :use-chips="nonEditableSelection"
    :emit-value="!returnObject"
    hide-bottom-space
    hide-dropdown-icon
    map-options
    outlined
    no-error-icon
    :virtual-scroll-slice-size="1000"
    @focus="
      () => {
        isFocused = true
      }
    "
    @blur="
      () => {
        inputValue = null
        $emit('blur')
        isFocused = false
      }
    "
    @input-value="onInput"
    @new-value="onCreate"
    @popup-show="onMenuShow"
    @popup-hide="onMenuHide"
    @update:model-value="onSelect"
  >
    <template v-if="ds.screen().isMobile && model === null" #prepend>
      <p-text-form-body :text="placeholder" :color="Color.GREY_400" :max-lines="PMaxLines.ONE" />
    </template>

    <template v-if="slots['options-header'] || isCreateBtnVisible" #before-options>
      <slot name="options-header" />
      <template v-if="isCreateBtnVisible">
        <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />

        <p-space :mobile="PSpaceSize.SM" vertical />
      </template>
    </template>

    <template v-if="nonEditableSelection" #selected-item="{ opt }: { opt: TItem }">
      <div class="p-flex p-items-center p-min-width-0" @mousedown.prevent>
        <template v-if="!!slots['selected-item']">
          <slot name="selected-item" :item="opt" />
        </template>

        <template v-else>
          {{ selectedItemLabel ?? itemLabel(opt) }}
        </template>
      </div>
    </template>

    <template
      #option="{
        itemProps,
        opt,
        selected,
        index,
      }: {
        itemProps: object
        opt: TItem | GroupHeader | GroupedOption<TItem>
        selected: boolean
        index: number
      }"
    >
      <template v-if="!!groups">
        <!-- One root element per virtual-scroll item: QSelect's virtual scroll maps each direct
             child of the options content to a single index, so each option must render exactly
             one wrapper element (otherwise sibling dividers/spaces corrupt the size table and
             break scroll position / keyboard scrollTo). -->
        <div v-if="isGroupHeader(opt)" :key="`header-${opt.key}`">
          <slot :name="`group-${opt.key}-label`">
            <p-text-form-body :text="opt.label" :color="Color.GREY_500" />
          </slot>

          <p-space :mobile="PSpaceSize.SM" vertical />
        </div>

        <div v-if="isGroupedOption(opt)" :key="opt.__value">
          <q-item v-bind="itemProps" :data-cy="`item-${opt.__value}`">
            <div class="p-flex p-items-center p-flex-1 p-min-width-0">
              <template v-if="!!slots['item']">
                <slot name="item" :item="opt.__original" :is-selected="selected" />
              </template>

              <p-text-form-body
                v-else
                :text="opt.__label"
                :color="selected ? Color.PRIMARY : Color.GREY_600"
              />
            </div>
          </q-item>

          <p-divider />
          <p-space
            v-if="
              (opt as GroupedOption<TItem>).__isLastInGroup &&
              !(opt as GroupedOption<TItem>).__isLastGroup
            "
            :mobile="PSpaceSize.LG"
            vertical
          />
        </div>
      </template>

      <div v-else :key="String((opt as TItem)[props.itemValue])">
        <q-item v-bind="itemProps">
          <div class="p-flex p-items-center p-flex-1 p-min-width-0">
            <template v-if="!!slots['item']">
              <slot name="item" :item="opt as TItem" :is-selected="selected" />
            </template>

            <p-text-form-body
              v-else
              :text="itemLabel(opt as TItem)"
              :color="selected ? Color.PRIMARY : Color.GREY_600"
            />
          </div>
        </q-item>

        <p-divider v-if="index < flatGroupOptions.length - 1" />
      </div>
    </template>

    <template #append>
      <template v-if="isEnableClearable">
        <q-icon
          class="p-cursor-pointer"
          :color="Color.GREY_400"
          name="fa-light fa-xmark"
          @mousedown.stop.prevent
          @click.stop.prevent="clear()"
        />
      </template>
      <p-icon icon="search" />
    </template>

    <template v-if="isNoOptionSlotVisible" #no-option>
      <div v-if="isCreateBtnVisible" class="p-ma-sm">
        <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />
      </div>
      <q-item v-if="loading && hasInputValue && !!slots['loading']">
        <div class="p-flex p-flex-center p-flex-1">
          <slot name="loading" />
        </div>
      </q-item>
      <q-item v-if="!loading && hasInputValue">
        <div class="p-flex p-flex-center">
          <p-text-form-body :text="noData || $t('No items match the value')" />
        </div>
      </q-item>
    </template>
  </q-select>

  <mobile-suggestion-dialog v-model="isMobileDialogShow" @dismiss="emits('mobile-dialog-dismiss')">
    <template #input>
      <p-text-input-field
        end-icon="magnifying-glass"
        :placeholder="$t('Search')"
        :debounce="inputDebounce"
        :loading="loading"
        autofocus
        cy="search-input"
        @update:model-value="onInput"
      />
    </template>

    <template #default>
      <slot name="options-header" />
      <template v-if="groups">
        <div v-if="isCreateBtnVisible">
          <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />
          <p-space :mobile="PSpaceSize.SM" vertical />
        </div>

        <div
          v-for="opt in flatGroupOptions"
          :key="
            isGroupHeader(opt)
              ? `header-${opt.key}`
              : isGroupedOption(opt)
                ? opt.__value
                : String((opt as TItem)[itemValue as keyof TItem])
          "
        >
          <div v-if="isGroupHeader(opt)">
            <p-space v-if="!opt.__isFirstGroup" :mobile="PSpaceSize.LG" vertical />
            <slot :name="`group-${opt.key}-label`">
              <p-text-form-body :text="opt.label" :color="Color.GREY_500" />
            </slot>

            <p-space :mobile="PSpaceSize.SM" vertical />
          </div>

          <template v-if="isGroupedOption(opt)">
            <q-item clickable style="padding: 0; min-height: auto" @click="selectGroupedItem(opt)">
              <p-card :pa-mobile="PCardSize.MD" class="p-flex-1 p-min-width-0">
                <template v-if="!!slots['item']">
                  <slot name="item" :item="opt.__original" :is-selected="model === opt.__value" />
                </template>

                <p-text-form-body
                  v-else
                  :text="opt.__label"
                  :color="model === opt.__value ? Color.PRIMARY : Color.GREY_600"
                />
              </p-card>
            </q-item>

            <p-divider />
            <p-space
              v-if="
                (opt as GroupedOption<TItem>).__isLastInGroup &&
                !(opt as GroupedOption<TItem>).__isLastGroup
              "
              :mobile="PSpaceSize.LG"
              vertical
            />
          </template>
        </div>
      </template>

      <q-virtual-scroll v-else :items="flatGroupOptions" separator class="p-flex-1">
        <template v-if="isCreateBtnVisible" #before>
          <p-card class="p-flex p-justify-center p-items-center">
            <create-btn :text="create" :end-icon="createEndIcon" @click="onCreate(inputValue)" />

            <q-item v-if="itemsFiltered.length === 0">
              <div class="p-flex p-flex-center">
                <p-text-form-body :text="noData || $t('No items match the value')" />
              </div>
            </q-item>
          </p-card>
        </template>

        <template #default="{ item }">
          <q-item clickable @click="selectItem(item)">
            <div class="p-flex p-items-center p-flex-1 p-min-width-0">
              <template v-if="!!slots['item']">
                <slot name="item" :item="item" :is-selected="isSelected(item[itemValue])" />
              </template>

              <p-text-form-body
                v-else
                :text="itemLabel(item)"
                :color="isSelected(item[itemValue]) ? Color.PRIMARY : Color.GREY_600"
              />
            </div>
          </q-item>
        </template>
      </q-virtual-scroll>
    </template>
  </mobile-suggestion-dialog>
</template>
